import { Injectable, inject } from '@angular/core';
import mapboxgl from 'mapbox-gl';
import { IReporteBase, IVistaMarker } from 'modelos/src';
import { VehicleIconGeneratorService } from './vehicle-icon-generator.service';
import { HelperService } from '../../servicios/helper.service';

/** Parámetros de alto nivel del ícono unificado de un vehículo. */
export interface MergedIconParams {
  reporte: IReporteBase<'Tracker 4G'>;
  vista?: IVistaMarker;
  grupoColor?: string;
  categoriaMarker: 'tipo' | 'funcion';
  isSelected: boolean;
  ringColor: string;
  orientation: number;
  mostrarFoto?: 'no' | 'centrado' | 'completo';
}

/**
 * Servicio coordinador para la generación de íconos compuestos de vehículos en Mapbox.
 *
 * Este servicio actúa como puente entre el componente de mapa y VehicleIconGeneratorService,
 * encargándose de:
 * - Mapear parámetros de alto nivel (IVistaMarker, reportes) a parámetros de generación
 * - Registrar ImageData generados en instancias de Mapbox
 * - Gestionar el ciclo de vida de imágenes registradas
 * - Limpiar imágenes no utilizadas para evitar memory leaks
 */
@Injectable({
  providedIn: 'root',
})
export class VehicleCompositeIconService {
  private iconGenerator = inject(VehicleIconGeneratorService);
  private helper = inject(HelperService);

  // Tracking de imágenes registradas por cada mapa
  // Key: instancia de mapa, Value: Set de keys de imágenes registradas
  private registeredImages = new Map<mapboxgl.Map, Set<string>>();

  // Listener de 'styleimagemissing' por mapa (para poder sacarlo en cleanupMap).
  private faltantesListeners = new Map<
    mapboxgl.Map,
    (e: { id: string }) => void
  >();

  /**
   * Red de seguridad: si Mapbox pide un ícono de vehículo que no está
   * registrado, se lo vuelve a registrar al instante desde el cache del
   * generador. Agregarlo dentro del evento evita el «Image … could not be
   * loaded» y que el marker quede sin ícono.
   *
   * Pasa cuando el estilo se rearma de cero al cambiar la capa base (Mapbox
   * descarta todas las imágenes y los features siguen apuntando a las keys) o
   * si una limpieza borra una key que un update concurrente acaba de asignar.
   * Si la key ya salió del cache LRU no hay nada que hacer acá: la próxima
   * pasada de íconos la regenera.
   */
  private escucharFaltantes(map: mapboxgl.Map): void {
    if (this.faltantesListeners.has(map)) return;
    const listener = (e: { id: string }) => {
      const id = e?.id;
      if (!id?.startsWith('vehicle-') || map.hasImage(id)) return;
      const cached = this.iconGenerator.getCachedIcon(id);
      if (!cached) return;
      map.addImage(id, cached, { pixelRatio: 1, sdf: false });
      this.trackImage(map, id);
    };
    this.faltantesListeners.set(map, listener);
    map.on('styleimagemissing', listener);
  }

  private getDefaultIcon(
    reporte: IReporteBase<'Tracker 4G'>,
    vista: 'tipo' | 'funcion',
  ): string {
    const tipo = reporte.activo?.vehiculo?.tipo;
    const funcion = reporte.activo?.funcion;

    if (vista === 'tipo') {
      switch (tipo) {
        case 'Auto':
          return 'assets/map/vehiculos/Markers Default/Tipo/auto.png';
        case 'Camion':
          return 'assets/map/vehiculos/Markers Default/Tipo/camion.png';
        case 'Camioneta':
          return 'assets/map/vehiculos/Markers Default/Tipo/camioneta.png';
        case 'Colectivo':
          return 'assets/map/vehiculos/Markers Default/Tipo/colectivo.png';
        case 'Grua':
          return 'assets/map/vehiculos/Markers Default/Tipo/grua.png';
        case 'Moto':
          return 'assets/map/vehiculos/Markers Default/Tipo/moto.png';
        case 'Otro':
          return 'assets/map/vehiculos/Markers Default/Tipo/otro.png';
        default:
          return 'assets/map/vehiculos/Markers Default/Tipo/otro.png';
      }
    }

    if (vista === 'funcion') {
      switch (funcion) {
        case 'Ambulancia':
          return 'assets/map/vehiculos/Markers Default/Funcion/ambulancia.png';
        case 'Bomberos':
          return 'assets/map/vehiculos/Markers Default/Funcion/bomberos.png';
        case 'Mantenimiento':
          return 'assets/map/vehiculos/Markers Default/Funcion/mantenimiento.png';
        case 'Otro':
          return 'assets/map/vehiculos/Markers Default/Funcion/otro.png';
        case 'Particular':
          return 'assets/map/vehiculos/Markers Default/Funcion/particular.png';
        case 'Policia':
          return 'assets/map/vehiculos/Markers Default/Funcion/policia.png';
        case 'Seguridad Privada':
          return 'assets/map/vehiculos/Markers Default/Funcion/seguridad_privada.png';
        case 'Servicio Técnico':
          return 'assets/map/vehiculos/Markers Default/Funcion/servicio tecnico.png';
        case 'Transporte':
          return 'assets/map/vehiculos/Markers Default/Funcion/transporte.png';
        default:
          return 'assets/map/vehiculos/Markers Default/Funcion/otro.png';
      }
    }

    return 'assets/map/vehiculos/Markers Default/Tipo/otro.png';
  }

  /**
   * Asegura que un ícono compuesto esté generado y registrado en el mapa.
   *
   * Este método:
   * 1. Extrae datos relevantes del reporte
   * 2. Calcula colores según IVistaMarker
   * 3. Genera el ícono compuesto (sin anillo de orientación) usando VehicleIconGeneratorService
   * 4. Registra el ImageData en Mapbox si no existe (con pixelRatio correcto)
   * 5. Retorna la key única del ícono para usarla en 'icon-image'
   *
   * @param map Instancia del mapa de Mapbox donde registrar la imagen
   * @param params Parámetros de configuración del ícono
   * @returns Promise<string> Key única del ícono compuesto
   */
  async ensureCompositeIcon(
    map: mapboxgl.Map,
    params: {
      reporte: IReporteBase<'Tracker 4G'>;
      vista?: IVistaMarker;
      grupoColor?: string;
      categoriaMarker: 'tipo' | 'funcion';
      isSelected: boolean;
    },
  ): Promise<string> {
    // 1. Calcular color del ícono
    let iconColor: string | undefined;
    if (params.vista?.useOriginalIconColor) {
      iconColor = undefined; // Sin tinte, usar imagen original
    } else if (params.grupoColor && params.vista?.groupColorIcon) {
      iconColor = params.grupoColor;
    } else {
      iconColor =
        params.categoriaMarker === 'tipo'
          ? params.vista?.vehiculeTypeIconColor
          : params.vista?.vehicleFunctionIconColor;
    }

    // Si no se pudo determinar, usar color por defecto
    if (!iconColor && !params.vista?.useOriginalIconColor) {
      iconColor = '#5196ff';
    }

    // 2. Calcular color de fondo
    let backgroundColor = '#ffffff';
    if (params.grupoColor && params.vista?.groupColorBackground) {
      backgroundColor = params.grupoColor;
    }

    // 3. Determinar URL del ícono según categoría
    const iconUrl =
      params.categoriaMarker === 'tipo'
        ? params.vista?.vehicleTypeIcon
        : params.vista?.vehicleFunctionIcon;

    if (!iconUrl) {
      // Usar ícono por defecto
      const defaultIconUrl = this.getDefaultIcon(
        params.reporte,
        params.categoriaMarker,
      );

      const { key, imageData } = await this.iconGenerator.generateVehicleIcon({
        iconUrl: defaultIconUrl,
        backgroundColor,
        iconColor,
        isSelected: params.isSelected,
      });

      // Registrar en Mapbox si no existe
      if (!map.hasImage(key)) {
        map.addImage(key, imageData, {
          pixelRatio: 1, // IMPORTANTE: Usar 1 para tamaño consistente cross-platform (canvas fijo 800/960px)
          sdf: false, // IMPORTANTE: NO usar SDF, es ImageData pre-renderizado
        });

        // Registrar en tracking
        this.trackImage(map, key);
      }

      return key;
    }

    // 4. Generar ícono compuesto (usa cache interno de VehicleIconGeneratorService)
    const { key, imageData } = await this.iconGenerator.generateVehicleIcon({
      iconUrl,
      backgroundColor,
      iconColor,
      isSelected: params.isSelected,
    });

    // 7. Registrar ImageData en Mapbox si no existe
    if (!map.hasImage(key)) {
      map.addImage(key, imageData, {
        pixelRatio: 1, // IMPORTANTE: Usar 1 para tamaño consistente cross-platform (canvas fijo 800/960px)
        sdf: false, // IMPORTANTE: NO usar SDF, es ImageData pre-renderizado con colores
      });

      // Registrar en tracking para cleanup posterior
      this.trackImage(map, key);
    }

    return key;
  }

  /**
   * Asegura que un ícono unificado (anillo bakeado + fondo + ícono) esté generado y registrado.
   *
   * Reemplaza la combinación ensureCompositeIcon + ensureOrientationRing para usar
   * una sola imagen por vehículo, eliminando los problemas de z-ordering entre markers.
   */
  async ensureMergedIcon(
    map: mapboxgl.Map,
    params: MergedIconParams,
  ): Promise<string> {
    const resuelto = this.resolveMergedIcon(params);

    // Ya registrado en este mapa: nada que generar.
    if (map.hasImage(resuelto.key)) {
      this.trackImage(map, resuelto.key);
      return resuelto.key;
    }

    if (resuelto.tipo === 'foto') {
      const { key, imageData } =
        await this.iconGenerator.generateMergedVehiclePhotoIcon(resuelto.args);

      if (!map.hasImage(key)) {
        map.addImage(key, imageData, { pixelRatio: 1, sdf: false });
        this.trackImage(map, key);
      }

      return key;
    }

    const { key, imageData } =
      await this.iconGenerator.generateMergedVehicleIcon(resuelto.args);

    if (!map.hasImage(key)) {
      map.addImage(key, imageData, {
        pixelRatio: 1,
        sdf: false,
      });
      this.trackImage(map, key);
    }

    return key;
  }

  /**
   * Devuelve la key del ícono unificado SOLO si ya está disponible (registrada
   * en el mapa o en el cache del generador), sin generar nada.
   *
   * Permite pintar la flota en un primer pase sincrónico con los íconos que ya
   * existen y dejar para una segunda pasada (en paralelo) los que hay que
   * generar — que en modo foto implican una carga de red por activo.
   */
  peekMergedIcon(map: mapboxgl.Map, params: MergedIconParams): string | null {
    const { key } = this.resolveMergedIcon(params);

    if (map.hasImage(key)) {
      this.trackImage(map, key);
      return key;
    }

    // Ya generado (otro mapa / carga anterior): registrarlo es sincrónico,
    // el ImageData está en el cache del generador.
    const cached = this.iconGenerator.getCachedIcon(key);
    if (cached) {
      map.addImage(key, cached, { pixelRatio: 1, sdf: false });
      this.trackImage(map, key);
      return key;
    }

    return null;
  }

  /**
   * Resuelve los parámetros de alto nivel (reporte + vista) a los argumentos
   * concretos del generador y su key. Compartido por ensureMergedIcon y
   * peekMergedIcon para que la key sea siempre la misma.
   */
  private resolveMergedIcon(params: MergedIconParams):
    | {
        tipo: 'foto';
        key: string;
        args: {
          photoUrl: string;
          backgroundColor: string;
          isSelected: boolean;
          ringColor: string;
          orientation: number;
          modo: 'centrado' | 'completo';
        };
      }
    | {
        tipo: 'icono';
        key: string;
        args: {
          iconUrl: string;
          backgroundColor: string;
          iconColor?: string;
          isSelected: boolean;
          ringColor: string;
          orientation: number;
        };
      } {
    // Calcular color de fondo (siempre necesario)
    let backgroundColor = '#ffffff';
    if (params.grupoColor && params.vista?.groupColorBackground) {
      backgroundColor = params.grupoColor;
    }

    // Modo foto: usar primera imagen del activo si está disponible
    const photoUrl =
      params.mostrarFoto && params.mostrarFoto !== 'no'
        ? (params.reporte.activo as any)?.imagenMiniatura ||
          params.reporte.activo?.imagenes?.[0]
        : undefined;

    if (photoUrl) {
      const args = {
        photoUrl,
        backgroundColor,
        isSelected: params.isSelected,
        ringColor: params.ringColor,
        orientation: params.orientation,
        modo: params.mostrarFoto as 'centrado' | 'completo',
      };
      return {
        tipo: 'foto',
        key: this.iconGenerator.mergedPhotoIconKey(args),
        args,
      };
    }

    // Calcular color del ícono
    let iconColor: string | undefined;
    if (params.vista?.useOriginalIconColor) {
      iconColor = undefined;
    } else if (params.grupoColor && params.vista?.groupColorIcon) {
      iconColor = params.grupoColor;
    } else {
      iconColor =
        params.categoriaMarker === 'tipo'
          ? params.vista?.vehiculeTypeIconColor
          : params.vista?.vehicleFunctionIconColor;
    }
    if (!iconColor && !params.vista?.useOriginalIconColor) {
      iconColor = '#5196ff';
    }

    // Determinar URL del ícono
    const iconUrl =
      params.categoriaMarker === 'tipo'
        ? params.vista?.vehicleTypeIcon
        : params.vista?.vehicleFunctionIcon;

    const args = {
      iconUrl:
        iconUrl || this.getDefaultIcon(params.reporte, params.categoriaMarker),
      backgroundColor,
      iconColor,
      isSelected: params.isSelected,
      ringColor: params.ringColor,
      orientation: params.orientation,
    };

    return {
      tipo: 'icono',
      key: this.iconGenerator.mergedIconKey(args),
      args,
    };
  }

  /**
   * Asegura que un anillo de orientación esté generado y registrado en el mapa.
   *
   * SOLUCIÓN PARA ARTEFACTOS DE TRANSPARENCIA:
   * En lugar de usar PNG con SDF + icon-color (que genera recuadros grises),
   * generamos el anillo dinámicamente con Canvas en el color exacto sin SDF.
   *
   * @param map Instancia del mapa de Mapbox donde registrar la imagen
   * @param color Color del anillo en formato hex
   * @param isSelected Si el marker está seleccionado (ajusta tamaño)
   * @returns string Key única del anillo generado
   */
  ensureOrientationRing(
    map: mapboxgl.Map,
    color: string,
    isSelected: boolean = false,
  ): string {
    // Generar key única basada en color y estado de selección
    const cleanColor = color.replace('#', '');
    const key = `orientation-ring-${cleanColor}-${isSelected ? 'sel' : 'nosel'}`;

    // Verificar si ya está registrado
    if (map.hasImage(key)) {
      return key;
    }

    // Generar anillo con Canvas (sin SDF)
    const size = isSelected ? 960 : 800; // Mismo tamaño que ícono compuesto
    const imageData = this.iconGenerator.generateOrientationRing(
      color,
      size,
      isSelected,
    );

    // Registrar en Mapbox sin SDF
    map.addImage(key, imageData, {
      pixelRatio: 1, // IMPORTANTE: Usar 1 para tamaño consistente cross-platform (canvas fijo 800/960px)
      sdf: false, // CRÍTICO: NO usar SDF para evitar artefactos
    });

    // Registrar en tracking
    this.trackImage(map, key);

    return key;
  }

  /**
   * Registra una imagen en el tracking de un mapa
   */
  private trackImage(map: mapboxgl.Map, imageKey: string): void {
    if (!this.registeredImages.has(map)) {
      this.registeredImages.set(map, new Set());
      this.escucharFaltantes(map);
    }
    this.registeredImages.get(map)!.add(imageKey);
  }

  /**
   * Limpia imágenes no utilizadas de un mapa.
   *
   * Este método compara las imágenes registradas con las keys actualmente en uso
   * y elimina las que ya no son necesarias, liberando memoria.
   *
   * @param map Instancia del mapa
   * @param usedKeys Set de keys de imágenes que están en uso
   */
  cleanupUnusedImages(map: mapboxgl.Map, usedKeys: Set<string>): void {
    const registered = this.registeredImages.get(map);
    if (!registered) return;

    const toRemove: string[] = [];

    for (const key of registered) {
      if (!usedKeys.has(key)) {
        toRemove.push(key);
      }
    }

    // Eliminar imágenes no utilizadas
    for (const key of toRemove) {
      if (map.hasImage(key)) {
        map.removeImage(key);
      }
      registered.delete(key);
    }
  }

  /**
   * Limpia todas las imágenes de un mapa.
   *
   * Útil cuando se destruye un mapa para liberar toda la memoria asociada.
   *
   * @param map Instancia del mapa
   */
  cleanupMap(map: mapboxgl.Map): void {
    const listener = this.faltantesListeners.get(map);
    if (listener) {
      this.faltantesListeners.delete(map);
      try {
        map.off('styleimagemissing', listener);
      } catch {
        // Mapa ya removido: no hay listener que sacar.
      }
    }

    const registered = this.registeredImages.get(map);
    if (!registered) return;

    let removedCount = 0;

    // Soltar la referencia al mapa siempre, aunque ya esté removido y
    // hasImage/removeImage fallen: el servicio es root y la retendría.
    this.registeredImages.delete(map);

    for (const key of registered) {
      try {
        if (map.hasImage(key)) {
          map.removeImage(key);
          removedCount++;
        }
      } catch (error) {
        // Mapa ya removido: el resto fallaría igual.
        console.warn(`⚠️ Error al eliminar imagen ${key}:`, error);
        break;
      }
    }
  }

  /**
   * Obtiene estadísticas del servicio para debugging
   */
  getStats(): {
    totalMaps: number;
    totalImages: number;
    imagesByMap: { mapId: number; imageCount: number }[];
  } {
    const stats = {
      totalMaps: this.registeredImages.size,
      totalImages: 0,
      imagesByMap: [] as { mapId: number; imageCount: number }[],
    };

    let mapIndex = 0;
    for (const [map, images] of this.registeredImages.entries()) {
      stats.totalImages += images.size;
      stats.imagesByMap.push({
        mapId: mapIndex++,
        imageCount: images.size,
      });
    }

    return stats;
  }
}
